/**
 * Один канал подій на вкладку — на всіх сторінках, не лише в таблиці.
 *
 * Сервер шле, що саме змінилося: позначки летять операціями прямо в
 * сховище, а люди, довідники, бланки й налаштування — сигналом «перечитай
 * bootstrap». Кожна подія несе ревізію курсу: якщо між двома сусідніми
 * подіями є пропуск (канал рвався, ноутбук спав), вкладка перечитує все, а
 * не сподівається, що нічого не сталося.
 *
 * Той самий канал каже, хто ще зараз у програмі.
 */

import {
  EVENT_STREAM_PATH,
  PRESENCE_PATH,
  type PresenceResponse,
  type PresenceUser,
  type ServerEvent,
} from '@kancelaria/shared';
import { create } from 'zustand';
import { reads } from '../api/inflight.js';
import { api } from '../api/client.js';
import { useData } from './data.js';
import { useLink } from './link.js';
import { useMarks } from './marks.js';

interface LiveState {
  /** Чи живий канал подій зараз. */
  connected: boolean;
  /** Хто тримає відкриту вкладку цього курсу, включно з нами. */
  online: PresenceUser[];
  connect: () => () => void;
  fetchPresence: () => Promise<void>;
}

/** Остання ревізія, яку бачила ця вкладка; `null` — ще жодної. */
let lastRev: number | null = null;

/** Перечитати все: довідкові дані й позначки відкритого періоду. */
function resync(): void {
  reads.invalidate();
  void useData.getState().refresh();
  void useMarks.getState().reload();
}

/**
 * Звірити ревізію події з попередньою. Повертає `true`, якщо між ними щось
 * загубилося і дані вже перечитуються — тоді саму подію застосовувати марно.
 */
function trackRev(rev: number): boolean {
  const prev = lastRev;
  lastRev = prev === null ? rev : Math.max(prev, rev);
  if (prev === null || rev <= prev + 1) return false;
  resync();
  return true;
}

function handle(event: ServerEvent): void {
  switch (event.type) {
    case 'ping':
      // Перша подія після (пере)підключення. Ревізія інша — щось було без нас.
      if (lastRev !== null && event.rev !== lastRev) resync();
      lastRev = event.rev;
      return;
    case 'marks':
      reads.invalidate();
      if (trackRev(event.rev)) return;
      useMarks.getState().applyRemote(event);
      return;
    case 'people':
    case 'settings':
      reads.invalidate();
      if (trackRev(event.rev)) return;
      void useData.getState().refresh();
      return;
    case 'presence':
      useLive.setState({ online: event.online });
      return;
  }
}

export const useLive = create<LiveState>(set => ({
  connected: false,
  online: [],

  connect: () => {
    // Новий канал — нова точка відліку: після переходу в інший курс ревізії
    // не порівнюються зі старими.
    lastRev = null;
    const source = new EventSource(EVENT_STREAM_PATH);
    source.onmessage = (ev: MessageEvent<string>) => {
      let data: ServerEvent;
      try {
        data = JSON.parse(ev.data) as ServerEvent;
      } catch {
        return;
      }
      handle(data);
    };
    source.onopen = () => {
      set({ connected: true });
      useLink.getState().setOnline(true);
    };
    // Браузер перепідключає EventSource сам; поки канал лежить, список
    // присутніх недостовірний — краще порожній, ніж застарілий.
    source.onerror = () => set({ connected: false, online: [] });
    return () => {
      source.close();
      set({ connected: false, online: [] });
    };
  },

  fetchPresence: async () => {
    try {
      const res = await api.get<PresenceResponse>(PRESENCE_PATH);
      set({ online: res.online });
    } catch {
      /* без зв'язку список і так порожній */
    }
  },
}));
